import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, CreditCard, BarChart3, Settings, UserCog, Target, Dumbbell, Crown, Menu, X, Ticket, UserCheck, Receipt, Bell, Search, HeartPulse, Filter, Building2, PanelLeftClose, PanelLeftOpen, Wallet } from 'lucide-react';
import { cn } from '@techsofit/ui';
import { api, useAuth } from '@techsofit/app-kit';
import { GymDataProvider } from '../data/GymDataProvider';
import { CommandPalette } from './CommandPalette';
import { QuickActions } from './QuickActions';
import { AccountMenu } from './AccountMenu';
import { WelcomeCard } from './WelcomeCard';
import { fetchTenant, type TenantBranding } from '../lib/accountApi';
import { getPlans, listMembers, getStaff } from '../lib/gymApi';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  /** Owner-only modules are hidden from staff accounts. */
  ownerOnly?: boolean;
}

const NAV: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/members', label: 'Members', icon: Users },
  { to: '/leads', label: 'Leads', icon: Filter },
  { to: '/health', label: 'Member health', icon: HeartPulse },
  { to: '/corporate', label: 'Corporate', icon: Building2 },
  { to: '/guests', label: 'Guests', icon: Ticket },
  { to: '/attendance', label: 'Attendance', icon: UserCheck },
  { to: '/workouts', label: 'Workouts', icon: Dumbbell },
  { to: '/goal-check-ins', label: 'Goal check-ins', icon: Target },
  { to: '/payments', label: 'Payments', icon: Receipt },
  { to: '/finance', label: 'Finance', icon: Wallet, ownerOnly: true },
  { to: '/plans', label: 'Plans', icon: CreditCard },
  { to: '/notifications', label: 'Notifications', icon: Bell },
  { to: '/reports', label: 'Reports', icon: BarChart3 },
  { to: '/billing', label: 'Billing', icon: Crown, ownerOnly: true },
  { to: '/staff', label: 'Staff', icon: UserCog, ownerOnly: true },
  { to: '/settings', label: 'Settings', icon: Settings },
];

/** Read the per-device "welcome dismissed" flag, tolerant of blocked storage. */
function readDismissed(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function SidebarLink({ item, onNavigate, collapsed }: { item: NavItem; onNavigate?: () => void; collapsed?: boolean }) {
  const { label, to, icon: Icon } = item;
  return (
    <NavLink
      to={to}
      onClick={onNavigate}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        cn(
          'group flex items-center rounded-control text-sm font-medium transition-colors duration-150',
          collapsed ? 'justify-center px-0 py-1.5' : 'gap-2 px-2.5 py-1',
          isActive ? 'bg-accent text-accent-ink' : 'text-on-dark-muted hover:bg-dark-soft hover:text-on-dark',
        )
      }
    >
      <Icon className="size-4 shrink-0" aria-hidden />
      {!collapsed && label}
    </NavLink>
  );
}

const SIDEBAR_KEY = 'tsf:sidebar-hidden';

function readSidebarHidden(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_KEY) === '1';
  } catch {
    return false;
  }
}

function writeSidebarHidden(hidden: boolean) {
  try {
    localStorage.setItem(SIDEBAR_KEY, hidden ? '1' : '0');
  } catch {
    // private mode / storage blocked
  }
}

/** The dark navigation panel — shared by the desktop sidebar and the mobile drawer. */
function NavPanel({
  items,
  displayName,
  gymName,
  email,
  logoUrl,
  collapsed,
  onHide,
  onNavigate,
  onSearch,
  onLogout,
}: {
  items: NavItem[];
  displayName: string;
  gymName: string;
  email: string;
  logoUrl?: string | null;
  collapsed?: boolean;
  onHide?: () => void;
  onNavigate?: () => void;
  onSearch: () => void;
  onLogout: () => void;
}) {
  return (
    <div className="flex h-full flex-col">
      <div className={cn('flex w-full min-w-0 items-center', collapsed ? 'justify-center py-1' : 'gap-2 px-1 py-1')}>
        {logoUrl ? (
          <img
            src={logoUrl}
            alt=""
            className="size-8 shrink-0 rounded-[0.6rem] bg-on-dark/10 object-contain p-0.5"
          />
        ) : (
          <span
            aria-hidden
            className="grid size-8 shrink-0 place-items-center rounded-[0.6rem] bg-on-dark/10 font-display text-sm font-semibold text-on-dark"
          >
            {(gymName.trim().charAt(0) || 'G').toUpperCase()}
          </span>
        )}
        {!collapsed && (
          <span className="min-w-0 flex-1 truncate font-display text-base font-semibold tracking-tight text-on-dark">
            {gymName}
          </span>
        )}
        {onHide && !collapsed && (
          <button
            type="button"
            onClick={onHide}
            aria-label="Hide sidebar"
            title="Hide sidebar"
            className="inline-flex size-7 shrink-0 items-center justify-center rounded-control text-on-dark-muted hover:bg-dark-soft hover:text-on-dark"
          >
            <PanelLeftClose className="size-4" aria-hidden />
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={() => {
          onNavigate?.();
          onSearch();
        }}
        title="Search"
        className={cn(
          'mt-2 flex w-full items-center rounded-control bg-dark-soft text-sm text-on-dark-muted transition-colors hover:text-on-dark',
          collapsed ? 'justify-center px-0 py-1.5' : 'gap-2 px-2.5 py-1.5',
        )}
      >
        <Search className="size-4 shrink-0" aria-hidden />
        {!collapsed && (
          <>
            <span className="flex-1 text-left">Search…</span>
            <kbd className="rounded border border-on-dark/15 px-1.5 py-0.5 text-[10px] tabular">⌘K</kbd>
          </>
        )}
      </button>

      <nav className="mt-3 flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto">
        {items.map((item) => (
          <SidebarLink key={item.to} item={item} onNavigate={onNavigate} collapsed={collapsed} />
        ))}
      </nav>

      <div className="mt-2">
        {!collapsed && (
          <AccountMenu displayName={displayName} gymName={gymName} email={email} onLogout={onLogout} />
        )}
      </div>
    </div>
  );
}

/** Desktop: dark rounded sidebar. Mobile: top bar with a hamburger that slides in the same nav. */
export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [sidebarHidden, setSidebarHidden] = useState(readSidebarHidden);

  function toggleSidebar() {
    setSidebarHidden((prev) => {
      const next = !prev;
      writeSidebarHidden(next);
      return next;
    });
  }

  // ⌘K / Ctrl+K opens the command palette from anywhere in the app.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const isOwner = user?.role === 'GYM_OWNER';
  const items = NAV.filter((item) => !item.ownerOnly || isOwner);

  const displayName = user?.fullName ?? '';
  const gymName = user?.gymName ?? '';
  const email = user?.email ?? '';

  // The gym's branding (logo) for the sidebar, from its subdomain. We wait for this to
  // resolve before deciding on the welcome card, so a set logo isn't misread as missing.
  const [tenant, setTenant] = useState<TenantBranding | null>(null);
  const [tenantLoaded, setTenantLoaded] = useState(false);
  useEffect(() => {
    fetchTenant()
      .then(setTenant)
      .catch(() => undefined)
      .finally(() => setTenantLoaded(true));
  }, []);
  const logoUrl = tenant?.logoUrl ?? null;

  // First-run welcome on the gym's own subdomain. It should only appear while setup is
  // actually unfinished — once logo + a plan + a member exist, it never shows again
  // (and we persist that so we don't even fetch progress next time).
  const tenantBase = import.meta.env.VITE_TENANT_BASE_DOMAIN as string | undefined;
  const onGymSubdomain = !!tenantBase && window.location.hostname.endsWith(`.${tenantBase}`);
  const welcomeKey = `tsf:welcome-dismissed:${window.location.hostname}`;
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  // staff > 1 means a teammate beyond the owner has been invited (the optional step).
  const [progress, setProgress] = useState({ plans: false, members: false, staff: false });
  // Decide once per mount, after branding is known, so we never flash a "3 of 3 done" card.
  const decided = useRef(false);
  useEffect(() => {
    if (decided.current || !tenantLoaded) return;
    if (!onGymSubdomain || !gymName || readDismissed(welcomeKey)) {
      decided.current = true;
      return;
    }
    decided.current = true;
    let alive = true;
    Promise.all([
      getPlans().then((p) => p.length > 0).catch(() => false),
      listMembers({ size: 1 }).then((m) => m.items.length > 0).catch(() => false),
      isOwner ? getStaff().then((s) => s.length > 1).catch(() => false) : Promise.resolve(false),
    ]).then(([plans, members, staff]) => {
      if (!alive) return;
      setProgress({ plans, members, staff });
      // Required steps complete → retire the card for good; otherwise show it.
      if (!!logoUrl && plans && members) {
        try {
          localStorage.setItem(welcomeKey, '1');
        } catch {
          // private mode / storage blocked — it'll just re-check next time
        }
      } else {
        setWelcomeOpen(true);
      }
    });
    return () => {
      alive = false;
    };
  }, [tenantLoaded, onGymSubdomain, gymName, welcomeKey, isOwner, logoUrl]);

  // Close the drawer whenever we navigate, and lock background scroll while it's open.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false);
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [menuOpen]);

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  async function exitSupport() {
    await api('/account-auth/support-exit', { method: 'POST' }).catch(() => undefined);
    const admin = (import.meta.env.VITE_ADMIN_URL as string | undefined)?.replace(/\/$/, '') || 'http://localhost:5174';
    window.location.assign(admin);
  }

  const support = user?.support;

  return (
    <div className="flex min-h-dvh flex-col bg-canvas">
      {support && (
        <div className="sticky top-0 z-40 flex h-10 shrink-0 items-center justify-between gap-3 bg-ink px-4 text-sm text-on-dark">
          <p className="min-w-0 truncate font-medium">
            Support view · {support.gymName || gymName}
          </p>
          <button
            type="button"
            onClick={exitSupport}
            className="shrink-0 rounded-pill bg-accent px-3 py-1 text-xs font-semibold text-accent-ink hover:bg-accent/90"
          >
            Exit to console
          </button>
        </div>
      )}
      <div className="flex min-h-0 flex-1">
        {/* ── Desktop sidebar ─────────────────────────────── */}
        <aside
          className={cn(
            'sticky hidden shrink-0 bg-dark md:block',
            support ? 'top-10 h-[calc(100dvh-2.5rem)]' : 'top-0 h-dvh',
            sidebarHidden ? 'w-14 p-2' : 'w-56 p-3',
          )}
        >
          <NavPanel
            items={items}
            displayName={displayName}
            gymName={gymName}
            email={email}
            logoUrl={logoUrl}
            collapsed={sidebarHidden}
            onHide={toggleSidebar}
            onSearch={() => setSearchOpen(true)}
            onLogout={handleLogout}
          />
        </aside>

        {/* ── Main content ────────────────────────────────── */}
        <main className="min-w-0 flex-1 px-4 py-4 md:px-6 md:py-5">
          {sidebarHidden && (
            <div className="mb-2 hidden md:block">
              <button
                type="button"
                onClick={toggleSidebar}
                aria-label="Show sidebar"
                className="inline-flex items-center gap-1.5 rounded-control bg-card px-2.5 py-1 text-xs font-medium text-ink-soft shadow-card hover:text-ink"
              >
                <PanelLeftOpen className="size-3.5" aria-hidden />
                Show sidebar
              </button>
            </div>
          )}
          {/* Mobile top bar */}
          <header className="mb-3 flex items-center justify-between md:hidden">
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              aria-expanded={menuOpen}
              className="inline-flex size-touch items-center justify-center rounded-control bg-card text-ink shadow-card active:scale-95"
            >
              <Menu className="size-5" aria-hidden />
            </button>
            {logoUrl ? (
              <img src={logoUrl} alt={gymName} className="h-8 max-w-[10rem] object-contain" />
            ) : (
              <span className="flex items-center gap-2">
                <span
                  aria-hidden
                  className="grid size-8 shrink-0 place-items-center rounded-control bg-dark font-display text-sm font-semibold text-on-dark"
                >
                  {(gymName.trim().charAt(0) || 'G').toUpperCase()}
                </span>
                <span className="max-w-[10rem] truncate font-display text-base font-semibold text-ink">{gymName}</span>
              </span>
            )}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                aria-label="Search"
                className="inline-flex size-touch items-center justify-center rounded-control bg-card text-ink shadow-card active:scale-95"
              >
                <Search className="size-5" aria-hidden />
              </button>
              <QuickActions variant="icon" />
            </div>
          </header>

          <GymDataProvider>
            <Outlet />
          </GymDataProvider>
        </main>
      </div>

      {/* ── Mobile drawer ───────────────────────────────── */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true" aria-label="Menu">
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setMenuOpen(false)}
            className="absolute inset-0 bg-ink/40 backdrop-blur-sm animate-fade-in"
          />
          <div className="absolute inset-y-0 left-0 flex w-[17rem] max-w-[82vw] flex-col bg-dark p-4 shadow-pop animate-slide-in-left">
            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              aria-label="Close menu"
              className="absolute right-3 top-3 inline-flex size-9 items-center justify-center rounded-control text-on-dark-muted hover:bg-dark-soft hover:text-on-dark"
            >
              <X className="size-5" aria-hidden />
            </button>
            <NavPanel
              items={items}
              displayName={displayName}
              gymName={gymName}
              email={email}
              logoUrl={logoUrl}
              onNavigate={() => setMenuOpen(false)}
              onSearch={() => setSearchOpen(true)}
              onLogout={handleLogout}
            />
          </div>
        </div>
      )}

      <CommandPalette open={searchOpen} onClose={() => setSearchOpen(false)} />

      <WelcomeCard
        open={welcomeOpen}
        onClose={() => setWelcomeOpen(false)}
        gymName={gymName}
        host={window.location.host}
        origin={window.location.origin}
        isOwner={isOwner}
        hasLogo={!!logoUrl}
        hasPlans={progress.plans}
        hasMembers={progress.members}
        hasStaff={progress.staff}
      />
    </div>
  );
}
